import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Api } from '../lib/api.js';
import { useWallet } from '../lib/wallet.jsx';
import { Button, Field } from '../components/ui.jsx';
import { JourneySteps, JourneyNext } from '../components/Journey.jsx';

/**
 * Deploy an agent — capability platform.
 *
 * A person picks a preset (the default is a full CEO agent), toggles
 * recommended capability boxes, tunes personality/tone and response style,
 * and can add fully custom instructions. Everything selected is composed into
 * the agent's SOUL.md — what you turn on is literally what the runtime uses on
 * every task (§8: customization is real, not cosmetic) — so the SOUL.md is
 * shown live and stays fully editable.
 *
 * Creating an agent is off-chain (a draft). Minting its soulbound passport is
 * the on-chain step and happens on the Agents screen.
 *
 * Icons are custom monoline pictograms (no emoji) drawn inline as SVG so they
 * inherit brand color and stay crisp at any size.
 */

// Custom pictograms — one per capability id. Line art, currentColor stroke.
const ICON = {
  organizations: (
    <>
      <path d="M3 8 L12 3 L21 8" />
      <path d="M3 20 H21" />
      <path d="M6 8 V20 M10 8 V20 M14 8 V20 M18 8 V20" />
    </>
  ),
  research: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M20 20 L16 16" />
    </>
  ),
  coordination: (
    <>
      <circle cx="12" cy="12" r="2.2" />
      <circle cx="5" cy="6" r="2" />
      <circle cx="19" cy="6" r="2" />
      <circle cx="12" cy="20" r="2" />
      <path d="M10.5 10.6 L6.2 7.4 M13.5 10.6 L17.8 7.4 M12 14.2 V18" />
    </>
  ),
  planning: (
    <>
      <path d="M6 3 V21" />
      <path d="M6 4 H17 L14 7.5 L17 11 H6" />
    </>
  ),
  analysis: (
    <>
      <path d="M4 20 H21" />
      <path d="M7 20 V13 M12 20 V8 M17 20 V11" />
    </>
  ),
  finance: (
    <>
      <ellipse cx="12" cy="7" rx="7" ry="3" />
      <path d="M5 7 V13 C5 14.7 8.1 16 12 16 C15.9 16 19 14.7 19 13 V7" />
      <path d="M5 13 V17 C5 18.7 8.1 20 12 20 C15.9 20 19 18.7 19 17 V13" />
    </>
  ),
  markets: (
    <>
      <path d="M4 16 L9 11 L13 14 L20 6" />
      <path d="M15 6 H20 V11" />
    </>
  ),
  writing: (
    <>
      <path d="M4 20 L5.2 15.8 L16 5 L19 8 L8.2 18.8 Z" />
      <path d="M14 7 L17 10" />
    </>
  ),
  outreach: (
    <>
      <circle cx="8" cy="8" r="2.6" />
      <circle cx="16" cy="8" r="2.6" />
      <path d="M3.5 19 C3.5 15.5 6 14 8 14 C10 14 12.5 15.5 12.5 19" />
      <path d="M11.5 19 C11.5 15.5 14 14 16 14 C18 14 20.5 15.5 20.5 19" />
    </>
  ),
  automation: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2 V5 M12 19 V22 M2 12 H5 M19 12 H22 M4.9 4.9 L7 7 M17 17 L19.1 19.1 M19.1 4.9 L17 7 M7 17 L4.9 19.1" />
    </>
  ),
  coding: (
    <>
      <path d="M8 8 L4 12 L8 16 M16 8 L20 12 L16 16 M13.5 6 L10.5 18" />
    </>
  ),
  product: (
    <>
      <path d="M12 3 L20 7.5 V16.5 L12 21 L4 16.5 V7.5 Z" />
      <path d="M4 7.5 L12 12 L20 7.5 M12 12 V21" />
    </>
  ),
  design: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 15.5 L9 10.5 L13 14.5 L16 11.5 L20 15.5" />
      <circle cx="15" cy="8.5" r="1.4" />
    </>
  ),
  growth: (
    <>
      <path d="M12 3 C15 6 16 9.5 16 13 L8 13 C8 9.5 9 6 12 3 Z" />
      <circle cx="12" cy="9" r="1.5" />
      <path d="M8 13 L5 17 L8 16 M16 13 L19 17 L16 16" />
      <path d="M10 18 L12 21 L14 18" />
    </>
  ),
  legal: (
    <>
      <path d="M12 4 V20 M8 20 H16" />
      <path d="M5 7 H19" />
      <path d="M12 4 L5 7 M12 4 L19 7" />
      <path d="M2.5 12 A2.5 2.5 0 0 0 7.5 12 Z" />
      <path d="M16.5 12 A2.5 2.5 0 0 0 21.5 12 Z" />
    </>
  ),
  negotiation: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  community: (
    <>
      <path d="M5 4 H19 A2 2 0 0 1 21 6 V13 A2 2 0 0 1 19 15 H11 L6 19 V15 H5 A2 2 0 0 1 3 13 V6 A2 2 0 0 1 5 4 Z" />
      <path d="M8 9.5 H8.01 M12 9.5 H12.01 M16 9.5 H16.01" />
    </>
  ),
  _check: (
    <path d="M5 12.5 L10 17.5 L19 6.5" />
  ),
  _plus: (
    <path d="M12 5 V19 M5 12 H19" />
  ),
  support: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.5" />
      <path d="M12 3 V8.5 M12 15.5 V21 M3 12 H8.5 M15.5 12 H21" />
    </>
  ),
};

function Icon({ id, className = 'deploy__ico' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON[id] ?? null}
    </svg>
  );
}

// Each capability is a clickable box. `soul` is the section injected into the
// agent's SOUL.md when the box is on.
const CAPABILITIES = [
  {
    id: 'organizations',
    name: 'Organizations',
    tag: 'Maps and runs org structures',
    soul:
      'ORGANIZATIONS: You understand company and org structures. Map entities, roles, ownership and reporting lines. Track who owns what, surface gaps, and keep an accurate picture of every organization in scope.',
  },
  {
    id: 'research',
    name: 'Research',
    tag: 'Live web research with sources',
    soul:
      'RESEARCH: Find current information on the web and report what is actually happening, with source URLs. Lead with the answer, then the evidence. Never present a guess as a fact.',
  },
  {
    id: 'coordination',
    name: 'Coordination',
    tag: 'Orchestrates people and agents',
    soul:
      'COORDINATION: Break goals into owners, dependencies and deadlines. Delegate to the right specialist, chase what is blocked, and keep every workstream moving toward the goal.',
  },
  {
    id: 'planning',
    name: 'Strategy',
    tag: 'Plans, roadmaps, execution',
    soul:
      'STRATEGY: Turn objectives into concrete plans, roadmaps and checklists. Sequence work by impact, name the critical path, and keep every recommendation action-oriented.',
  },
  {
    id: 'analysis',
    name: 'Analysis',
    tag: 'Reads data, finds the signal',
    soul:
      'ANALYSIS: Work through data and numbers precisely. Separate what is confirmed from what is inferred, quantify where possible, and state the confidence behind each conclusion.',
  },
  {
    id: 'finance',
    name: 'Finance',
    tag: 'Models money and runway',
    soul:
      'FINANCE: Track revenue, cost, runway and valuation. Build clear models, flag the numbers that matter, and be exact about assumptions versus actuals.',
  },
  {
    id: 'markets',
    name: 'Markets',
    tag: 'Tokens, equities, crypto moves',
    soul:
      'MARKETS: Track tokens, equities and crypto. Explain moves with current data and sources, and be precise about what is confirmed versus speculation.',
  },
  {
    id: 'writing',
    name: 'Writing',
    tag: 'Drafts and polishes anything',
    soul:
      'WRITING: Draft and refine emails, posts and documents. Match the requested tone, keep it tight, and deliver one clear version rather than many options.',
  },
  {
    id: 'outreach',
    name: 'Outreach',
    tag: 'BD, partnerships, deal flow',
    soul:
      'OUTREACH: Handle business development and partnerships. Open conversations naturally, qualify fit, and move relationships toward a concrete next step without hard-selling.',
  },
  {
    id: 'automation',
    name: 'Automation',
    tag: 'Builds repeatable workflows',
    soul:
      'AUTOMATION: Turn recurring tasks into reliable workflows. Define triggers, steps and checks so work runs the same way every time with minimal supervision.',
  },
  {
    id: 'coding',
    name: 'Engineering',
    tag: 'Writes and reviews code',
    soul:
      'ENGINEERING: Write, review and debug code. Match the project stack and conventions, ship working examples, and verify before claiming a fix.',
  },
  {
    id: 'product',
    name: 'Product',
    tag: 'Specs, features, roadmaps',
    soul:
      'PRODUCT: Turn user problems into crisp specs and feature definitions. Prioritise by impact and effort, define acceptance criteria, and keep scope honest.',
  },
  {
    id: 'design',
    name: 'Design',
    tag: 'UX, visuals, brand feel',
    soul:
      'DESIGN: Think in user flows, hierarchy and visual clarity. Give concrete UI/UX guidance, respect brand and accessibility, and favour clean, usable layouts.',
  },
  {
    id: 'growth',
    name: 'Growth',
    tag: 'Acquisition, funnels, virality',
    soul:
      'GROWTH: Drive acquisition and retention. Design funnels and loops, propose testable experiments, and tie every tactic to a measurable metric.',
  },
  {
    id: 'legal',
    name: 'Legal',
    tag: 'Contracts, structure, risk',
    soul:
      'LEGAL: Handle contracts, corporate structure and compliance at a practical level. Flag risk plainly, suggest standard protections, and note when a licensed lawyer is required — never give definitive legal advice.',
  },
  {
    id: 'negotiation',
    name: 'Negotiation',
    tag: 'Deals, terms, leverage',
    soul:
      'NEGOTIATION: Prepare and run negotiations. Identify leverage and BATNA, anchor deliberately, hold to walk-away points, and drive toward terms that hold up.',
  },
  {
    id: 'community',
    name: 'Community',
    tag: 'Social, mods, engagement',
    soul:
      'COMMUNITY: Manage social channels and community. Read the room, keep engagement healthy, respond in the brand voice, and escalate anything sensitive.',
  },
  {
    id: 'support',
    name: 'Support',
    tag: 'Handles user questions',
    soul:
      'SUPPORT: Resolve user questions patiently and accurately. Diagnose the real issue, give clear steps, and confirm the problem is actually solved.',
  },
];

const CAP = Object.fromEntries(CAPABILITIES.map((c) => [c.id, c]));

// Personality / tone traits — optional, multi-select. Each adds one line.
const TRAITS = [
  { id: 'direct', name: 'Direct', soul: 'Be direct and blunt. Say what you mean without hedging.' },
  { id: 'concise', name: 'Concise', soul: 'Keep answers short and dense. No filler, no restating the question.' },
  { id: 'warm', name: 'Warm', soul: 'Be warm and encouraging in tone while staying useful.' },
  { id: 'formal', name: 'Formal', soul: 'Use a professional, formal register.' },
  { id: 'casual', name: 'Casual', soul: 'Keep it relaxed and conversational.' },
  { id: 'contrarian', name: 'Contrarian', soul: 'Push back when the user is likely wrong. Offer the counter-case.' },
  { id: 'proactive', name: 'Proactive', soul: 'Anticipate the next step and offer it without being asked.' },
  { id: 'noemoji', name: 'No emojis', soul: 'Never use emojis.' },
];
const TRAIT = Object.fromEntries(TRAITS.map((t) => [t.id, t]));

// Response length — single select. Optional (defaults to balanced).
const LENGTHS = [
  { id: 'terse', name: 'Terse', soul: 'Default to the shortest answer that is still complete.' },
  { id: 'balanced', name: 'Balanced', soul: 'Match answer length to the task — brief when simple, thorough when complex.' },
  { id: 'thorough', name: 'Thorough', soul: 'Default to detailed, well-structured answers with reasoning shown.' },
];
const LEN = Object.fromEntries(LENGTHS.map((l) => [l.id, l]));

// Presets seed a name, persona, capabilities, traits and length.
const PRESETS = [
  {
    id: 'ceo',
    name: 'CEO Agent',
    tagline: 'Runs everything end to end',
    persona:
      'You are a CEO-level agent. You hold the whole picture across organizations, strategy and execution, make decisions with judgment, and drive every objective to a result. Lead with the decision, then the reasoning.',
    caps: ['organizations', 'research', 'coordination', 'planning', 'analysis', 'finance'],
    traits: ['direct', 'concise', 'proactive'],
    length: 'balanced',
    featured: true,
  },
  {
    id: 'research',
    name: 'Research Analyst',
    tagline: 'Live web research with sources',
    persona:
      'You are a research analyst. Find current information and report what is actually happening, with sources. Lead with the answer, then the evidence.',
    caps: ['research', 'analysis'],
    traits: ['direct'],
    length: 'thorough',
  },
  {
    id: 'operator',
    name: 'Chief of Staff',
    tagline: 'Plans, organises, executes',
    persona:
      'You are a chief-of-staff agent. Break goals into concrete steps, keep answers action-oriented, and drive execution across every workstream.',
    caps: ['coordination', 'planning', 'analysis'],
    traits: ['direct', 'proactive'],
    length: 'balanced',
  },
  {
    id: 'markets',
    name: 'Markets Watcher',
    tagline: 'Tracks tokens, markets, moves',
    persona:
      'You are a markets specialist. Track tokens, equities and crypto, and explain moves clearly with current data and sources.',
    caps: ['markets', 'research', 'analysis'],
    traits: ['direct', 'concise'],
    length: 'balanced',
  },
  {
    id: 'builder',
    name: 'Engineer',
    tagline: 'Writes, reviews and ships code',
    persona:
      'You are an engineering agent. Write, review and debug code, match the project conventions, and verify before you claim something works.',
    caps: ['coding', 'analysis', 'product'],
    traits: ['direct', 'concise'],
    length: 'balanced',
  },
  {
    id: 'writer',
    name: 'Writer',
    tagline: 'Drafts and polishes anything',
    persona:
      'You are a writing specialist. Draft and refine emails, posts and documents, matching the tone the person asks for.',
    caps: ['writing'],
    traits: ['warm'],
    length: 'balanced',
  },
  {
    id: 'growth',
    name: 'Growth Lead',
    tagline: 'Acquisition, funnels, outreach',
    persona:
      'You are a growth lead. Drive acquisition and retention with testable experiments, and tie every tactic to a metric.',
    caps: ['growth', 'markets', 'outreach', 'writing'],
    traits: ['direct', 'proactive'],
    length: 'balanced',
  },
];

// Build the SOUL.md the runtime will use from every selection.
function composeSoul({ name, persona, capIds, traitIds, length, custom }) {
  const lines = [`# ${name} — SOUL.md`, '', persona.trim(), ''];

  const chosenCaps = capIds.map((id) => CAP[id]).filter(Boolean);
  if (chosenCaps.length) {
    lines.push('## Capabilities', '');
    for (const c of chosenCaps) lines.push(`- ${c.soul}`);
    lines.push('');
  }

  const chosenTraits = traitIds.map((id) => TRAIT[id]).filter(Boolean);
  const lenRule = LEN[length];
  if (chosenTraits.length || lenRule) {
    lines.push('## Personality & tone', '');
    for (const t of chosenTraits) lines.push(`- ${t.soul}`);
    if (lenRule) lines.push(`- ${lenRule.soul}`);
    lines.push('');
  }

  lines.push(
    '## Operating rules',
    '- Be results-oriented. Prioritise what moves the goal forward.',
    '- When a task needs current facts, use the web and cite sources.',
    '- Never present a guess as a confirmed fact.',
    '',
  );

  const c = (custom || '').trim();
  if (c) {
    lines.push('## Custom instructions', '', c, '');
  }

  return lines.join('\n').trimEnd().slice(0, 4000);
}

export function DeployAgent({ pushToast }) {
  const navigate = useNavigate();
  const { sessionReady, isAuthenticated, sendIntent } = useWallet();

  const [selected, setSelected] = useState(PRESETS[0].id);
  const [expanded, setExpanded] = useState(PRESETS[0].id);
  const [capIds, setCapIds] = useState(PRESETS[0].caps);
  const [traitIds, setTraitIds] = useState(PRESETS[0].traits);
  const [length, setLength] = useState(PRESETS[0].length);
  const [custom, setCustom] = useState('');
  const [name, setName] = useState(PRESETS[0].name);
  const [showSoul, setShowSoul] = useState(false);
  const [soulEdit, setSoulEdit] = useState(null); // null = auto-composed
  const [busy, setBusy] = useState(false);
  const [deployed, setDeployed] = useState(false);

  const preset = useMemo(() => PRESETS.find((p) => p.id === selected) ?? PRESETS[0], [selected]);

  const composedSoul = useMemo(
    () => composeSoul({ name: name || preset.name, persona: preset.persona, capIds, traitIds, length, custom }),
    [name, preset, capIds, traitIds, length, custom],
  );
  const soul = soulEdit ?? composedSoul;

  const recompose = () => setSoulEdit(null);

  const pickPreset = (p) => {
    setSelected(p.id);
    setExpanded(p.id);
    setCapIds(p.caps);
    setTraitIds(p.traits);
    setLength(p.length);
    setName(p.name);
    setSoulEdit(null); // recompose from the new preset
  };

  const toggleCap = (id) => {
    recompose();
    setCapIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const toggleTrait = (id) => {
    recompose();
    setTraitIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const pickLength = (id) => {
    recompose();
    setLength(id);
  };

  const deploy = async () => {
    if (busy || !sessionReady) return;
    setBusy(true);
    try {
      const finalName = (name.trim() || preset.name).slice(0, 120);
      const finalSoul = (soul.trim() || composedSoul).slice(0, 4000);
      const { agent } = await Api.createAgent({ name: finalName, role: finalSoul });
      // Record the deployment on chain (wallet-scoped, no passport required):
      // deploying an agent is a meaningful interaction, so it fires MetaMask
      // from tap one for any signed-in wallet. The DB draft is already saved,
      // so a rejected or failed signature never loses the agent.
      if (isAuthenticated) {
        try {
          const { intent } = await Api.walletDeployIntent();
          const r = await sendIntent({ intent, txType: 'wallet_deploy' });
          if (r.status === 'success') {
            pushToast?.({ tone: 'success', message: `${finalName} deployed and recorded on chain.` });
          }
        } catch (err) {
          pushToast?.({ tone: 'danger', message: `${finalName} saved. On-chain record failed: ${err.message}` });
        }
      } else {
        pushToast?.({ tone: 'info', message: `${finalName} saved. Connect and sign in to record it on chain.` });
      }
      setDeployed(true);
      navigate(`/agents/${agent.id}`);
    } catch (err) {
      pushToast?.({ tone: 'danger', message: err.message });
      setBusy(false);
    }
  };

  return (
    <div className="stack deploy">
      <JourneySteps current={1} />
      <header className="page-header">
        <div>
          <h1 className="page-title">Deploy an agent</h1>
          <p className="page-subtitle">
            Start from a preset, then tune the capabilities, personality and instructions. Everything you turn on is written into the agent's SOUL.md and used on every task.
          </p>
        </div>
      </header>

      <section className="deploy__presets">
        {PRESETS.map((p) => {
          const on = selected === p.id;
          const open = true; // all presets show full detail; CEO is just Recommended
          const showCaps = on ? capIds : p.caps;
          return (
            <article
              key={p.id}
              className={`deploy__preset${on ? ' deploy__preset--on' : ''}${p.featured ? ' deploy__preset--feat' : ''}`}
              role="button"
              tabIndex={0}
              aria-pressed={on}
              onClick={() => pickPreset(p)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  pickPreset(p);
                }
              }}
            >
              <div className="deploy__preset-head">
                <span className="deploy__preset-title">
                  {p.name}
                  {p.featured ? <span className="deploy__badge">Recommended</span> : null}
                </span>
                <span className="deploy__preset-tag">{p.tagline}</span>
              </div>
              <div className={`deploy__preset-detail${open ? ' deploy__preset-detail--open' : ''}`}>
                <div className="deploy__anim" aria-hidden="true">
                  {showCaps.map((id, i) => (
                    <span
                      key={id}
                      className="deploy__orb"
                      style={{ animationDelay: `${i * 90}ms` }}
                      title={CAP[id]?.name}
                    >
                      <Icon id={id} />
                    </span>
                  ))}
                </div>
                <p className="deploy__preset-desc">{p.persona}</p>
                <ul className="deploy__feature-list">
                  {showCaps.map((id) => (
                    <li key={id}>
                      <span className="deploy__feature-icon"><Icon id={id} /></span>
                      <span>
                        <strong>{CAP[id]?.name}</strong> — {CAP[id]?.tag}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          );
        })}
      </section>

      <section className="deploy__caps">
        <div className="deploy__caps-head">
          <h2 className="deploy__section-title">Capabilities</h2>
          <span className="deploy__caps-count">{capIds.length} selected</span>
        </div>
        <p className="deploy__section-hint">Click to add or remove. Each one adds a real section to the SOUL.md.</p>
        <div className="deploy__caps-grid">
          {CAPABILITIES.map((c) => {
            const on = capIds.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                className={`deploy__cap${on ? ' deploy__cap--on' : ''}`}
                aria-pressed={on}
                onClick={() => toggleCap(c.id)}
              >
                <span className="deploy__cap-icon"><Icon id={c.id} /></span>
                <span className="deploy__cap-name">{c.name}</span>
                <span className="deploy__cap-tag">{c.tag}</span>
                <span className="deploy__cap-check">{on ? <Icon id="_check" className="deploy__ico" /> : <Icon id="_plus" className="deploy__ico" />}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="deploy__tune">
        <div className="deploy__tune-col">
          <h2 className="deploy__section-title">Personality &amp; tone</h2>
          <p className="deploy__section-hint">Optional. Pick any that fit.</p>
          <div className="deploy__chips">
            {TRAITS.map((t) => {
              const on = traitIds.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`deploy__chip${on ? ' deploy__chip--on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggleTrait(t.id)}
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="deploy__tune-col">
          <h2 className="deploy__section-title">Response length</h2>
          <p className="deploy__section-hint">How much detail by default.</p>
          <div className="deploy__chips">
            {LENGTHS.map((l) => {
              const on = length === l.id;
              return (
                <button
                  key={l.id}
                  type="button"
                  className={`deploy__chip${on ? ' deploy__chip--on' : ''}`}
                  aria-pressed={on}
                  onClick={() => pickLength(l.id)}
                >
                  {l.name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="deploy__setup">
        <Field label="Agent name" htmlFor="deploy-name">
          <input
            id="deploy-name"
            className="input"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field
          label="Custom instructions"
          htmlFor="deploy-custom"
          hint="Optional. Anything specific — context, rules, do's and don'ts. Added verbatim to the SOUL.md."
        >
          <textarea
            id="deploy-custom"
            className="input deploy__brief"
            rows={5}
            placeholder="e.g. Always answer in English. Focus on the $100M path. Never mention raise figures publicly."
            value={custom}
            maxLength={2000}
            onChange={(e) => {
              recompose();
              setCustom(e.target.value);
            }}
          />
        </Field>

        <button type="button" className="deploy__soul-toggle" onClick={() => setShowSoul((v) => !v)}>
          {showSoul ? 'Hide' : 'Preview / edit'} SOUL.md ({soul.length} chars)
        </button>

        {showSoul ? (
          <Field
            label="SOUL.md"
            htmlFor="deploy-soul"
            hint="Composed from every selection above. Edit freely — this is exactly what the agent uses."
          >
            <textarea
              id="deploy-soul"
              className="input deploy__brief"
              rows={16}
              value={soul}
              maxLength={4000}
              onChange={(e) => setSoulEdit(e.target.value)}
            />
          </Field>
        ) : null}

        <div className="deploy__actions">
          <Button variant="primary" loading={busy} onClick={deploy} disabled={!capIds.length}>
            Deploy {name.trim() || preset.name}
          </Button>
          {soulEdit != null ? (
            <Button variant="ghost" onClick={() => setSoulEdit(null)} disabled={busy}>
              Reset to composed
            </Button>
          ) : null}
        </div>
      </section>

      <JourneyNext current={1} done={deployed} />
    </div>
  );
}
